import React from 'react';
import 'banner-pattern/dist/main.css';
import { buildClassPrefix, formatErrorMessage } from '../../utils/errorPatterns';

interface TuitionErrorBannerProps {
    currentStep: number;
    errors: Record<string, string>;
    fieldLabels?: Record<string, string>;
    onFieldClick?: (fieldName: string) => void;
    formData?: Record<string, any>;
}

// ==================== UTILITY TYPES ====================

interface ClassFieldMatch {
    isClassField: boolean;
    baseFieldName: string;
    classIndex: number;
    classNumber: number;
}

interface ElementSelector {
    id: string;
    dateInputId: string;
    textareaId: string;
    nameAttr: string;
}



// ==================== UTILITY FUNCTIONS ====================

/**
 * Generate selector combinations for finding field elements
 */
const generateSelectors = (fieldName: string): ElementSelector => ({
    id: fieldName,
    dateInputId: `date-input-${fieldName}`,
    textareaId: `textarea-${fieldName}`,
    nameAttr: fieldName,
});

/**
 * Find field element by ID using selector strategy
 */
const findElementById = (selectors: ElementSelector): HTMLElement | null => {
    return (
        document.getElementById(selectors.id) ||
        document.getElementById(selectors.dateInputId) ||
        document.getElementById(selectors.textareaId)
    );
};

/**
 * Find field element by name attribute (fallback strategy)
 */
const findElementByName = (selectors: ElementSelector): HTMLElement | null => {
    const element = document.querySelector(`[name="${selectors.nameAttr}"]`);
    return element as HTMLElement | null;
};

/**
 * Find and focus on form input within container element
 */
const focusNestedInput = (container: HTMLElement): void => {
    const inputElement =
        container.querySelector('input') ||
        container.querySelector('select') ||
        container.querySelector('textarea');

    if (inputElement) {
        (inputElement as HTMLElement).focus();
    } else {
        container.focus();
    }
};

/**
 * Focus on element, handling different element types appropriately
 */
const focusElement = (element: HTMLElement): void => {
    if (element.tagName === 'SELECT') {
        element.focus();
    } else if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
        element.focus();
    } else {
        focusNestedInput(element);
    }
};

/**
 * Scroll to and focus on field element
 */
const scrollToField = (fieldName: string): void => {
    const selectors = generateSelectors(fieldName);

    // Try ID-based search first, fallback to name attribute search
    let targetElement = findElementById(selectors);
    targetElement ??= findElementByName(selectors);

    if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        focusElement(targetElement);
    }
};

/**
 * Parse field name to extract class information
 */
const parseClassField = (fieldName: string): ClassFieldMatch => {
    const classRegex = /^(.+)_class(\d+)$/;
    const classMatch = classRegex.exec(fieldName);

    if (classMatch) {
        const classIndex = Number.parseInt(classMatch[2], 10);
        return {
            isClassField: true,
            baseFieldName: classMatch[1],
            classIndex,
            classNumber: classIndex + 1,
        };
    }

    return {
        isClassField: false,
        baseFieldName: fieldName,
        classIndex: -1,
        classNumber: 1,
    };
};





/**
 * Get field label from labels map, with fallback to field name
 */
const getFieldLabel = (
    baseFieldName: string,
    fieldLabels: Record<string, string>
): string => {
    return fieldLabels[baseFieldName] || baseFieldName;
};

/**
 * Validate date format (MM/DD/YYYY)
 */
const isValidDateFormat = (dateString: string): boolean => {
    if (!dateString) return false;
    const dateRegex = /^(0?\d|1[0-2])\/(0?\d|[12]\d|3[01])\/\d{4}$/;
    return dateRegex.test(String(dateString));
};

/**
 * Filter out eligibility error from errors object
 */
const filterFieldErrors = (errors: Record<string, string>): Record<string, string> => {
    const { eligibility, ...fieldErrors } = errors;
    return fieldErrors;
};

/**
 * Check if eligibility error should be displayed
 */
const shouldDisplayEligibilityError = (
    errors: Record<string, string>,
    courseStartDate: string
): boolean => {
    return 'eligibility' in errors && isValidDateFormat(courseStartDate);
};

// ==================== REUSABLE COMPONENTS ====================

/**
 * Error list item component
 */
interface ErrorListItemProps {
    fieldName: string;
    displayMessage: string;
    onFieldClick?: (fieldName: string) => void;
}

const ErrorListItem: React.FC<ErrorListItemProps> = ({
    fieldName,
    displayMessage,
    onFieldClick,
}) => (
    <button
        key={fieldName}
        type="button"
        onClick={() => {
            scrollToField(fieldName);
            onFieldClick?.(fieldName);
        }}
        className="!text-[#0074AD] underline text-left w-full"
        style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
        }}
    >
        <ul className="flex items-start list-disc !m-0 !ml-4">
            <li>
                <div className="flex-1">
                    <span className="text-[16px] leading-[24px]">
                        {displayMessage}
                    </span>
                </div>
            </li>
        </ul>
    </button>
);

/**
 * Error list component
 */
interface StandardErrorListProps {
    errors: Record<string, string>;
    currentStep: number;
    fieldLabels: Record<string, string>;
    onFieldClick?: (fieldName: string) => void;
}

const StandardErrorList: React.FC<StandardErrorListProps> = ({
    errors,
    currentStep,
    fieldLabels,
    onFieldClick,
}) => (
    <div className="mt-2 flex flex-col">
        {Object.entries(errors).map(([fieldName, errorMessage]) => {
            // If this is a global error (e.g., globalError or globalError_DUPLICATE_RECORD), show only the message
            if (fieldName === 'globalError' || fieldName.startsWith('globalError_')) {
                return (
                    <ErrorListItem
                        key={fieldName}
                        fieldName={fieldName}
                        displayMessage={errorMessage}
                        onFieldClick={onFieldClick}
                    />
                );
            }
            const classFieldInfo = parseClassField(fieldName);
            const fieldLabel = getFieldLabel(
                classFieldInfo.baseFieldName,
                fieldLabels
            );
            const classPrefix = buildClassPrefix(
                classFieldInfo.isClassField,
                classFieldInfo.classNumber,
                currentStep
            );
            const displayMessage = formatErrorMessage(errorMessage, fieldLabel, classPrefix);
            return (
                <ErrorListItem
                    key={fieldName}
                    fieldName={fieldName}
                    displayMessage={displayMessage}
                    onFieldClick={onFieldClick}
                />
            );
        })}
    </div>
);

/**
 * Banner title component
 */
const BannerTitle: React.FC = () => (
    <div
        className="ds-banner__main-header--awareness"
        id="tuition-error-banner-title"
        style={{ fontWeight: 350, fontSize: '20px' }}
    >
        There are errors that need your attention before submission:
    </div>
);

/**
 * Banner subtitle component
 */
interface BannerSubtitleProps {
    showEligibilityError: boolean;
    eligibilityMessage: string;
}

const BannerSubtitle: React.FC<BannerSubtitleProps> = ({
    showEligibilityError,
    eligibilityMessage,
}) => (
    <p className="p3 !mb-0">
        {showEligibilityError
            ? eligibilityMessage
            : 'Some fields contain invalid or missing information. Review the details below and update the highlighted fields to proceed.'}
    </p>
);

// ==================== MAIN COMPONENT ====================

const TuitionErrorBanner: React.FC<TuitionErrorBannerProps> = ({
    currentStep,
    errors,
    fieldLabels = {},
    onFieldClick,
    formData = {},
}) => {
    // Remove applicationYear error for non-admin (employee) flow
    const isAdmin = globalThis?.location?.pathname?.includes('/admin');
    const filteredErrors = { ...errors };
    if (!isAdmin && 'applicationYear' in filteredErrors) {
        delete filteredErrors.applicationYear;
    }

    const errorCount = Object.keys(filteredErrors).length;
    if (errorCount === 0) {
        return null;
    }

    // Determine eligibility error state
    const courseStartDate = formData?.courseStartDate ?? '';
    const showEligibilityError = shouldDisplayEligibilityError(filteredErrors, courseStartDate);
    const eligibilityMessage = showEligibilityError ? filteredErrors.eligibility : '';

    // Get field errors (excluding eligibility)
    const fieldErrors = filterFieldErrors(filteredErrors);
    const showFieldErrors = Object.keys(fieldErrors).length > 0;

    return (
        <div className="mb-6">
            <div
                className="ds-banner ds-banner__bg--white ds-banner--icon p-6!"
                data-ds-variant="basic"
                data-ds-version="1"
                data-ds-theme="vessel"
                aria-labelledby="tuition-error-banner-title"
                style={{ borderColor: '#D1480C' }}
            >
                <div className="ds-banner__icon-img">
                    <img
                        src="/assets/images/errorCircleSmall.svg"
                        alt=""
                        style={{ width: '42px', height: '42px' }}
                    />
                </div>

                <div className="ds-banner__container">
                    <div className="ds-banner__main flex flex-col">
                        <BannerTitle />

                        <BannerSubtitle
                            showEligibilityError={showEligibilityError}
                            eligibilityMessage={eligibilityMessage}
                        />

                        {showFieldErrors && (
                            <div className="ds-banner__main-body" style={{ fontSize: '16px' }}>
                                <StandardErrorList
                                    errors={fieldErrors}
                                    currentStep={currentStep}
                                    fieldLabels={fieldLabels}
                                    onFieldClick={onFieldClick}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TuitionErrorBanner;